import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { USER_ROLES, ROLE_LABELS } from '@logisticslash/shared';
import { useTheme } from '@/components/ThemeProvider';
import { useAuth } from '@/components/AuthProvider';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FloatingField } from '@/components/ui/floating-field';
import { PasswordField } from '@/components/ui/money-field';
import { DataTable } from '@/components/ui/data-table';
import { StatusPill } from '@/components/ui/status-pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const INVITE_ROLES = USER_ROLES.filter((r) => r !== 'super_admin');

export default function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionUserId, setActionUserId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetLinkShown, setResetLinkShown] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'dispatcher',
    linkedParty: '',
    password: '',
  });

  const dataQ = useQuery({
    queryKey: ['settings-page'],
    queryFn: async () => {
      const me = await api('/api/v1/auth/me');
      if (me.status !== 200) throw new Error(me.data?.error ?? 'Failed to load profile');
      const list = await api('/api/v1/auth/users');
      const p = await api('/api/v1/shippers');
      return {
        user: me.data.user ?? null,
        company: me.data.company ?? null,
        seats: me.data.seats ?? null,
        users: list.status === 200 ? list.data.users ?? [] : [],
        listError: list.status === 200 ? '' : 'User list requires admin role',
        parties: p.status === 200 ? p.data.shippers ?? [] : [],
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load settings');
  }, [dataQ.isError, dataQ.error]);

  const users = dataQ.data?.users ?? [];
  const company = dataQ.data?.company ?? null;
  const seats = dataQ.data?.seats ?? null;
  const parties = dataQ.data?.parties ?? [];
  const listError = dataQ.data?.listError ?? '';
  const meId = dataQ.data?.user?.id ?? '';

  const sendReset = async (userId) => {
    if (actionUserId) return;
    setActionUserId(userId);
    setResetLinkShown('');
    try {
      const r = await api(`/api/v1/auth/users/${userId}/password-reset`, {
        method: 'POST',
        body: {},
      });
      if (r.status === 200) {
        if (r.data?.emailSent) {
          toast.success(r.data?.message || `Password reset email sent to ${r.data?.email || 'user'}`);
        } else if (r.data?.passwordResetLink) {
          toast.message(r.data?.message || 'Email not sent — use the reset link below');
          setResetLinkShown(r.data.passwordResetLink);
          try {
            await navigator.clipboard.writeText(r.data.passwordResetLink);
            toast.message('Reset link copied to clipboard');
          } catch {
            /* link is shown on page */
          }
        } else {
          toast.message(r.data?.message || 'Reset requested (stub mode — no email)');
        }
      } else {
        toast.error(String(r.data?.error ?? 'Reset failed'));
      }
    } finally {
      setActionUserId('');
    }
  };

  const reactivateUser = async (userId) => {
    if (actionUserId) return;
    setActionUserId(userId);
    try {
      const r = await api(`/api/v1/auth/users/${userId}/reactivate`, {
        method: 'POST',
        body: {},
      });
      if (r.status === 200) {
        toast.success('User reactivated');
        qc.invalidateQueries({ queryKey: ['settings-page'] });
      } else {
        toast.error(String(r.data?.error ?? 'Reactivate failed'));
      }
    } finally {
      setActionUserId('');
    }
  };

  const deleteUser = async () => {
    if (!deleteTarget?.id || actionUserId) return;
    setActionUserId(deleteTarget.id);
    try {
      const r = await api(`/api/v1/auth/users/${deleteTarget.id}`, { method: 'DELETE' });
      if (r.status === 200) {
        toast.success('User deleted (deactivated)');
        setDeleteTarget(null);
        qc.invalidateQueries({ queryKey: ['settings-page'] });
      } else {
        toast.error(String(r.data?.error ?? 'Delete failed'));
      }
    } finally {
      setActionUserId('');
    }
  };

  const columns = [
    { key: 'displayName', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    {
      key: 'role',
      label: 'Role',
      render: (v) => <StatusPill status={v || 'active'} />,
    },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'actions',
      label: '',
      render: (_, row) => {
        const busy = actionUserId === row.id;
        const isSelf = row.id === meId;
        const suspended = row.status === 'suspended';
        return (
          <div className="flex gap-1 justify-end">
            {!suspended ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void sendReset(row.id)}
                title="Send password reset email"
              >
                {busy ? (
                  <Icons.Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Icons.Mail className="h-3 w-3 mr-1" />
                )}
                Reset
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void reactivateUser(row.id)}
              >
                {busy ? (
                  <Icons.Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Icons.RotateCcw className="h-3 w-3 mr-1" />
                )}
                Reactivate
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={busy || isSelf || suspended}
              onClick={() => setDeleteTarget(row)}
              title={isSelf ? 'Cannot delete yourself' : 'Delete user'}
            >
              <Icons.Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        );
      },
    },
  ];

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.role) {
      toast.error('Please fill in email, name, and role');
      return;
    }
    if (!inviteForm.password || inviteForm.password.length < 8) {
      toast.error('Temporary password must be at least 8 characters');
      return;
    }
    if (inviteForm.role === 'customer' && !inviteForm.linkedParty) {
      toast.error('Customer invite requires a linked party');
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const r = await api('/api/v1/auth/users', {
        method: 'POST',
        body: {
          email: inviteForm.email.trim(),
          displayName: inviteForm.name.trim() || inviteForm.email.trim(),
          role: inviteForm.role,
          password: inviteForm.password,
          linkedPartyId: inviteForm.role === 'customer' ? inviteForm.linkedParty : undefined,
        },
      });
      if (r.status === 201) {
        toast.success(
          r.data?.passwordResetLink || r.data?.verificationLink
            ? 'Invite sent — set-password / verify links available if email is not configured'
            : 'User invited'
        );
        setInviteForm({
          email: '',
          name: '',
          role: 'dispatcher',
          linkedParty: '',
          password: '',
        });
        setShowInvite(false);
        qc.invalidateQueries({ queryKey: ['settings-page'] });
      } else {
        toast.error(String(r.data?.error ?? 'Invite failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Settings"
          description="Manage account and workspace preferences"
          icon="Settings"
          metrics={[
            { label: 'Company', value: company?.name ?? '—' },
            {
              label: 'Seats',
              value: seats ? `${seats.active}/${seats.limit}` : users.length || '—',
            },
            { label: 'Users', value: users.length },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize your workspace theme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium">Theme</p>
                  <p className="text-sm text-muted-foreground">
                    Current: {theme === 'light' ? 'Light' : 'Dark'} mode
                  </p>
                </div>
                <Button onClick={toggleTheme} variant="outline">
                  {theme === 'light' ? (
                    <>
                      <Icons.Moon className="h-4 w-4 mr-2" />
                      Dark
                    </>
                  ) : (
                    <>
                      <Icons.Sun className="h-4 w-4 mr-2" />
                      Light
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Company Info</CardTitle>
              <CardDescription>Your organization details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Company Name</p>
                <p className="font-medium">{company?.name ?? '—'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Plan</p>
                <StatusPill status="active">{company?.plan ?? '—'}</StatusPill>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Seats</p>
                <p className="font-medium font-mono">
                  {seats ? `${seats.active} / ${seats.limit}` : `${users.length}`}
                </p>
              </div>
              {company?.billingStatus ? (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Billing</p>
                  <StatusPill status={company.billingStatus} />
                </div>
              ) : null}
              {company?.trialEndsAt ? (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Trial ends</p>
                  <p className="font-medium font-mono text-sm">
                    {new Date(company.trialEndsAt).toLocaleString()}
                  </p>
                </div>
              ) : null}
              {company?.foundingMember ? (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Founding</p>
                  <p className="font-medium text-sm">Founding member</p>
                </div>
              ) : null}
              <div className="space-y-2 pt-2 border-t">
                <p className="text-sm text-muted-foreground">Manage billing</p>
                <Button type="button" variant="outline" disabled title="Lemon Squeezy — coming soon">
                  Manage billing (Lemon Squeezy — coming soon)
                </Button>
                <p className="text-xs text-muted-foreground">
                  Taxes and payments will be processed by Lemon Squeezy (Merchant of Record).
                </p>
                <a
                  className="text-xs text-primary underline"
                  href="mailto:support@logisticslash.com?subject=Data%20export%20or%20account%20deletion"
                >
                  Request data export / account deletion
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Team Members</CardTitle>
                <CardDescription>Manage user access and permissions</CardDescription>
              </div>
              {isAdmin ? (
                <Button onClick={() => setShowInvite(!showInvite)}>
                  <Icons.UserPlus className="h-4 w-4 mr-2" />
                  Invite User
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent>
            {listError ? (
              <p className="text-sm text-muted-foreground mb-4">{listError}</p>
            ) : null}
            {showInvite && isAdmin && (
              <div className="mb-6 p-4 border rounded-lg space-y-4 bg-muted/20">
                <h4 className="font-semibold text-sm">Invite New User</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FloatingField
                    label="Email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    data-testid="invite-email-input"
                  />
                  <FloatingField
                    label="Display Name"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    required
                    data-testid="invite-name-input"
                  />
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Role <span className="text-destructive">*</span>
                    </label>
                    <Select
                      value={inviteForm.role}
                      onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                    >
                      <SelectTrigger data-testid="invite-role-select">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {INVITE_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABELS[r] || r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <PasswordField
                    label="Temporary Password"
                    value={inviteForm.password}
                    onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                    required
                  />
                  {inviteForm.role === 'customer' && (
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Linked Party *
                      </label>
                      <Combobox
                        options={parties.map((p) => ({ value: p.id, label: p.name }))}
                        value={inviteForm.linkedParty}
                        onChange={(v) => setInviteForm({ ...inviteForm, linkedParty: v })}
                        placeholder="Select party"
                      />
                    </div>
                  )}
                  {inviteForm.role === 'driver' && (
                    <p className="md:col-span-2 text-xs text-muted-foreground">
                      Driver portal / My loads is not live yet. Prefer inviting drivers after the
                      drivers app launches. Match roster email when you do.
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowInvite(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleInvite}
                    disabled={saving}
                    data-testid="send-invite-button"
                  >
                    <Icons.Send className="h-4 w-4 mr-2" />
                    {saving ? 'Sending…' : 'Send Invite'}
                  </Button>
                </div>
              </div>
            )}
            {resetLinkShown ? (
              <div className="mb-4 p-3 border rounded-lg bg-muted/30 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Password reset link (share with user)
                </p>
                <p className="text-xs font-mono break-all">{resetLinkShown}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(resetLinkShown);
                        toast.success('Copied');
                      } catch {
                        toast.error('Could not copy — select the link manually');
                      }
                    }}
                  >
                    <Icons.Copy className="h-3 w-3 mr-1" />
                    Copy link
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setResetLinkShown('')}>
                    Dismiss
                  </Button>
                </div>
              </div>
            ) : null}
            <DataTable
              columns={columns}
              data={users}
              emptyMessage={dataQ.isLoading ? 'Loading…' : 'No users yet'}
            />
          </CardContent>
        </Card>

        <ConfirmDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          title="Delete user?"
          description={
            deleteTarget
              ? `${deleteTarget.displayName || deleteTarget.email} will be deactivated and lose access. This frees a seat. You can reactivate them later.`
              : ''
          }
          confirmLabel="Delete user"
          onConfirm={() => void deleteUser()}
        />
      </div>
);
}
